import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import AxiosOICStat from 'AxiosOICStat';
import { Button, Card, message, Table, Select, Row, Col } from 'antd';
import FormulaBuilder from '../components/FormulaBuilder';
import * as XLSX from 'xlsx';
import { writeExcelFile } from '../utils/excelExport';
import { getIndicatorName } from '../utils/indicatorName';

const CustomFormulaCalculation = () => {
    const [customFormula, setCustomFormula] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const indicators = useSelector(state => state.indicators.value);
    const countries = useSelector(state => state.countries.value);

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => 2000 + i);

    const handleCalculate = async () => {
        if (!customFormula.trim()) {
            message.warning('Please build a formula first');
            return;
        }
        setLoading(true);
        try {
            const response = await AxiosOICStat.post('/indicators/indicator-formula/calculate', { 
                formula: customFormula, 
                ind_code: 0
            });
            setResult(response.data);
        } catch (error) {
            message.error('Calculation failed');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!result || result.length === 0) {
            message.warning('No data to export');
            return;
        }
        const hasIndCode = result.some(r => r.ind_code && r.ind_code !== 0);
        const yearSet = new Set();
        const countryMap = new Map();
        result.forEach(item => {
            yearSet.add(item.d_year);
            if (!countryMap.has(item.c_code)) {
                countryMap.set(item.c_code, { ind_code: item.ind_code });
            }
            countryMap.get(item.c_code)[item.d_year] = item.d_value;
        });
        const years = Array.from(yearSet).sort();
        const indHeaders = hasIndCode ? ['Indicator Code', 'Indicator Name'] : [];
        const headers = [...indHeaders, 'Country Code', 'Country', ...years];
        const data = Array.from(countryMap.entries()).map(([c_code, yearValues]) => {
            const country = countries[c_code];
            const indCols = hasIndCode ? (() => {
                const ind = indicators && indicators[yearValues.ind_code];
                return [yearValues.ind_code, ind ? (getIndicatorName(ind) || ind.ind_name || yearValues.ind_code) : yearValues.ind_code];
            })() : [];
            const row = [...indCols, c_code, country ? country.c_short_name_eng : c_code];
            years.forEach(year => {
                row.push(yearValues[year] !== undefined ? yearValues[year] : '');
            });
            return row;
        });
        const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
        // Auto-fit column widths based on max content length
        const allRows = [headers, ...data];
        ws['!cols'] = headers.map((_, colIdx) => {
            const maxLen = allRows.reduce((max, row) => {
                const cell = row[colIdx];
                return Math.max(max, cell != null ? String(cell).length : 0);
            }, 0);
            return { wch: Math.min(maxLen + 2, 60) };
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Results");
        writeExcelFile(wb, "custom_formula_results.xlsx");
    };

    const columns = [
        ...(result && result.some(r => r.ind_code && r.ind_code !== 0) ? [
            { title: 'Indicator Code', dataIndex: 'ind_code', key: 'ind_code' },
            { title: 'Indicator Name', dataIndex: 'ind_code', key: 'ind_name',
                render: (ind_code) => {
                    const ind = indicators && indicators[ind_code];
                    return ind ? (getIndicatorName(ind) || ind.ind_name || ind_code) : ind_code;
                }
            }
        ] : []),
        { title: 'Country Code', dataIndex: 'c_code', key: 'c_code' },
        { title: 'Country Name', dataIndex: 'c_short_name_eng', key: 'c_short_name_eng',
            render: (text, record) => {
                const country = countries[record.c_code];
                return country ? country.c_short_name_eng : record.c_code;
            }
         },
        { title: 'Year', dataIndex: 'd_year', key: 'd_year' },
        { title: 'Value', dataIndex: 'd_value', key: 'd_value' },
    ];
    
    return (
        <div>
            <Card>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                </Row>
                <FormulaBuilder
                    indicators={indicators}
                    value={customFormula}
                    onChange={setCustomFormula}
                />
                <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col>
                        <Button type="primary" onClick={handleCalculate} loading={loading}>Calculate</Button>
                    </Col>
                    <Col>
                        <Button onClick={handleExport} disabled={!result || result.length === 0}>Export to Excel</Button>
                    </Col>
                </Row>
                {result && (
                    <Table
                        dataSource={result}
                        columns={columns}
                        rowKey={(record, index) => index}
                        style={{ marginTop: 16 }}
                        pagination={{ pageSize: 20 }}
                    />
                )}
            </Card>
        </div>
    );
};

export default CustomFormulaCalculation;